import { inject, Injectable, signal } from '@angular/core';
import { KeycloakService } from '@inoventoryapps/keycloak-angular';
import { sortBy } from 'lodash-es';
import { catchError, firstValueFrom, forkJoin, of } from 'rxjs';
import { ROLE_CONFIG } from '../constants/constants';
import { GeoLevel, LinkedVOCLF, MobileUserInfo, UserDetails, UserRoles } from '../models/user.model';
import { AdminService } from './admin.service';
import { GlobalService } from './global.service';
import { IncubatorService } from './incubator.service';
import { LocalStorageService } from './local-storage.service';
import { LokosService } from './lokos.service';

type GeoInput = Partial<{ blockId: string; districtId: string; stateId: string }>;

@Injectable({ providedIn: 'root' })
export class UserSessionService {
    private readonly keycloak = inject(KeycloakService);
    private readonly gs = inject(GlobalService);
    private readonly ls = inject(LocalStorageService);
    private readonly adminService = inject(AdminService);
    private readonly incubatorService = inject(IncubatorService);
    private readonly lokosService = inject(LokosService);

    readonly userDetails = signal<UserDetails>({
        activeGeoLevel: 'block',
        blockId: '',
        districtId: '',
        activeRoleOgName: '',
        mobileNumber: '',
        roles: [],
        stateId: '',
        userFullName: '',
        username: '',
        sub: '',
        imageData: ''
    });
    readonly activeRoleDisplayName = signal('');

    /** Must run after Keycloak init; resolves once userDetails is available in the signal and in storage. */
    async init(): Promise<void> {
        if (window.common) {
            await this.syncFromMobileHost();
            return;
        }

        if (!this.keycloak.isLoggedIn()) {
            // SSO session is gone, so the next login may be a different user
            this.clearSession();
            return;
        }

        let storedDetails = this.ls.get<UserDetails>('userDetails');
        if (storedDetails && storedDetails.sub !== this.keycloak.getKeycloakInstance().subject) {
            // Cached details belong to a previously logged-in user
            this.clearSession();
            storedDetails = undefined;
        }

        await firstValueFrom(this.gs.loadGeoMap());

        if (!(await this.restoreFromStorage(storedDetails))) {
            await this.buildFromKeycloak();
        }
    }

    async syncFromMobileHost() {
        if (!window.common) return;
        const details = window.common.getLoggedInUserDetails();

        const { userInfo, selectedRole, stateId, districtId, blockId, cboType, cboData }: MobileUserInfo =
            JSON.parse(details);
        const { name, sub } = userInfo;
        const imageData = await firstValueFrom(this.adminService.getUserPhoto());
        this.userDetails.set({
            activeGeoLevel: 'block',
            activeRoleOgName: selectedRole,
            mobileNumber: '',
            roles: [
                {
                    geoLevel: 'block',
                    roleDisplayName: selectedRole,
                    roleName: selectedRole,
                    roleOgName: selectedRole,
                    blockId: `${blockId}`
                }
            ],
            sub,
            userFullName: name,
            username: name,
            stateId: `${stateId}`,
            districtId: `${districtId}`,
            blockId: `${blockId}`,
            cboType,
            cboData,
            imageData
        });
        this.activeRoleDisplayName.set(selectedRole);
        this.persist();
    }

    private clearSession(): void {
        this.ls.clearStorage();
        sessionStorage.clear();
    }

    private persist(): void {
        this.ls.set<UserDetails>('userDetails', this.userDetails());
        this.ls.set<GeoLevel>('activeGeoLevel', this.userDetails().activeGeoLevel);
        this.ls.set('activeRoleOgName', this.userDetails().activeRoleOgName);
        this.ls.set('activeRoleDisplayName', this.activeRoleDisplayName());
    }

    private async restoreFromStorage(storedDetails?: UserDetails): Promise<boolean> {
        const storedGeoLevel = this.ls.get<GeoLevel>('activeGeoLevel');
        const storedRoleDisplayName = this.ls.get<string>('activeRoleDisplayName');
        const storedRoleOgName = this.ls.get<string>('activeRoleOgName');
        if (!storedDetails || !storedGeoLevel || !storedRoleDisplayName || !storedRoleOgName) {
            return false;
        }

        const geoInput: GeoInput = {};
        if (storedGeoLevel === 'block' && storedDetails.blockId) {
            geoInput.blockId = storedDetails.blockId.startsWith('B-')
                ? storedDetails.blockId
                : `B-${storedDetails.blockId}`;
        } else if (storedGeoLevel === 'district' && storedDetails.districtId) {
            geoInput.districtId = storedDetails.districtId.startsWith('D-')
                ? storedDetails.districtId
                : `D-${storedDetails.districtId}`;
        } else if (storedGeoLevel === 'state' && storedDetails.stateId) {
            geoInput.stateId = storedDetails.stateId.startsWith('S-')
                ? storedDetails.stateId
                : `S-${storedDetails.stateId}`;
        }
        const normalizedGeo = this.gs.getGeoLocation(geoInput, storedGeoLevel);

        const incubator: Partial<UserDetails> = {};
        if (storedRoleOgName === 'state-incubator-agency') {
            const mapping = await firstValueFrom(this.incubatorService.getUserIncubatorMapping(storedDetails.username));
            incubator.incubatorId = mapping.data[0]?.incubatorId;
        }

        this.userDetails.set({
            ...storedDetails,
            ...normalizedGeo,
            ...incubator,
            activeGeoLevel: storedGeoLevel,
            activeRoleOgName: storedRoleOgName
        });
        this.activeRoleDisplayName.set(storedRoleDisplayName);
        this.persist();
        return true;
    }

    private async buildFromKeycloak(): Promise<void> {
        const userInfo = await this.gs.getUserInfo();
        const userSubRoles = await firstValueFrom(this.adminService.getUserSubRoles(userInfo.sub));
        const userIsClfBookkeeper = userSubRoles.some(
            (permission) => permission === 'Institutional Building:CLF Bookkeeper'
        );
        const geoMap = { block: '', district: '', state: '', national: '' };
        let roles: UserRoles[] = [];
        const ibBlocks: UserRoles[] = [];

        for (const key in userInfo.resource_access) {
            const [prefix, geoId] = key.split('-');
            if (['B', 'D', 'S', 'N'].includes(prefix) && geoId) {
                userInfo.resource_access[key].roles.forEach((role) => {
                    if (
                        role.includes('block-manager-farm') ||
                        role.includes('block-manager-nonfarm') ||
                        role.includes('district-manager-farm') ||
                        role.includes('district-manager-nonfarm') ||
                        role.includes('district-pe-manager') ||
                        role.includes('state-incubator-agency') ||
                        role.includes('state-manager-farm') ||
                        role.includes('state-manager-nonfarm') ||
                        role.includes('state-nodal-agency') ||
                        role.includes('national-manager-farm') ||
                        role.includes('national-manager-nonfarm')
                    ) {
                        let geoLevel: GeoLevel;
                        const geo: GeoInput = {};

                        if (role.startsWith('block')) {
                            geoLevel = 'block';
                            geoMap.block = key;
                            geo.blockId = geoId;
                        } else if (role.startsWith('district')) {
                            geoLevel = 'district';
                            geoMap.district = key;
                            geo.districtId = geoId;
                        } else if (role.startsWith('state')) {
                            geoLevel = 'state';
                            geoMap.state = key;
                            geo.stateId = geoId;
                        } else {
                            geoLevel = 'national';
                            geoMap.national = key;
                        }

                        const { name, ogName } = ROLE_CONFIG[role];
                        roles.push({
                            geoLevel,
                            ...geo,
                            roleOgName: ogName,
                            roleDisplayName: `${name}${geoLevel === 'national' ? '' : ` (${geoId})`}`,
                            roleName: name
                        });
                    } else if (role.includes('block-cadre-ib') && userIsClfBookkeeper) {
                        geoMap.block = key;
                        ibBlocks.push({
                            geoLevel: 'block',
                            blockId: geoId,
                            roleOgName: 'clf-bookkeeper',
                            roleDisplayName: '',
                            roleName: 'CLF Bookkeeper'
                        });
                    }
                });
            }
        }

        let activeGeoLevel: GeoLevel;
        const geoInput: GeoInput = {};
        if (geoMap.block) {
            activeGeoLevel = 'block';
            geoInput.blockId = geoMap.block;
        } else if (geoMap.district) {
            activeGeoLevel = 'district';
            geoInput.districtId = geoMap.district;
        } else if (geoMap.state) {
            activeGeoLevel = 'state';
            geoInput.stateId = geoMap.state;
        } else {
            activeGeoLevel = 'national';
        }
        const normalizedGeo = this.gs.getGeoLocation(geoInput, activeGeoLevel);

        const mappingDetails = await firstValueFrom(
            forkJoin(
                ibBlocks.length
                    ? ibBlocks.map((block) => {
                          const normalizedIBGeo = this.gs.getGeoLocation({ blockId: `B-${block.blockId}` }, 'block');
                          return this.lokosService
                              .getLinkedVOCLF(
                                  normalizedIBGeo.stateId!,
                                  normalizedIBGeo.districtId!,
                                  normalizedIBGeo.blockId!,
                                  userInfo.sub,
                                  'CLF'
                              )
                              .pipe(catchError(() => of<LinkedVOCLF[]>([])));
                      })
                    : [of<LinkedVOCLF[]>([])]
            )
        );

        roles.push(
            ...mappingDetails.flatMap((linkedCLF) =>
                linkedCLF.map<UserRoles>((clf) => ({
                    blockId: `${clf.block_id!}`,
                    federationId: clf.federation_id,
                    geoLevel: 'block',
                    roleOgName: 'clf-bookkeeper',
                    roleDisplayName: `CLF Bookkeeper (${clf.federation_id})`,
                    roleName: 'CLF Bookkeeper'
                }))
            )
        );
        const allLinkedCLFs = sortBy(mappingDetails.flat(), 'federation_id');
        roles = sortBy(roles, 'roleDisplayName');

        const imageData = await firstValueFrom(this.adminService.getUserPhoto());

        const baseDetails: UserDetails = {
            roles,
            mobileNumber: userInfo.phone_number,
            userFullName: userInfo.name,
            username: userInfo.preferred_username,
            sub: userInfo.sub,
            imageData,
            // Default is first role
            activeRoleOgName: roles[0].roleOgName,
            activeGeoLevel,
            ...normalizedGeo
        };

        if (roles[0].roleOgName === 'clf-bookkeeper') {
            const activeCBO = allLinkedCLFs.find((clf) => clf.federation_id === roles[0].federationId)!;
            this.userDetails.set({
                ...baseDetails,
                cboData: allLinkedCLFs,
                activeCBOData: [activeCBO],
                activeFederationId: activeCBO.federation_id,
                cboType: 'CLF'
            });
        } else if (roles[0].roleOgName === 'state-incubator-agency') {
            const mapping = await firstValueFrom(
                this.incubatorService.getUserIncubatorMapping(userInfo.preferred_username)
            );
            this.userDetails.set({ ...baseDetails, incubatorId: mapping.data[0]?.incubatorId });
        } else {
            this.userDetails.set({ ...baseDetails, cboData: allLinkedCLFs });
        }
        this.activeRoleDisplayName.set(roles[0].roleDisplayName);
        this.persist();
    }
}
